import React, { ChangeEvent, useContext, useEffect, useState } from "react";
import IErrorDTO from "../../../models/DTO/IErrorDTO";
import { Socket } from "socket.io-client";
import IUserUI from "../../../models/IUser";
import ISupplierModel from "../../../models/ISupplierModel";
import IWarehouse from "../../../models/IWarehouse";
import IWarehouse_Transfer from "../../../models/IWarehouse_Transfer";
import OwTitle from "../../shared/OwTitle/OwTitle";
import OwLabel from "../../shared/OwLabel/OwLabel";
import OwSelectOption from "../../shared/OwSelectOption/OwSelectOption";
import OwButton from "../../shared/OwButton/OwButton";
import OwToggle from "../../shared/OwToggle/OwToggle";
import EndpointsEnum from "../../../models/EndpointsEnum";
import IProcessWarehouseTransferDTO from "../../../models/DTO/IProcessWarehouseTransferDTO";
import IGetWarehousesDTO from "../../../models/DTO/IGetWarehousesDTO";
import IOnGetWarehousesDTO from "../../../models/DTO/IOnGetWarehousesDTO";
import SupplierContext from "../../../contexts/SupplierContext";
import UserContext from "../../../contexts/UserContext";

type Props = {
    cancelFn: (refresh?: boolean) => void;
    notifyFn: (data: IErrorDTO | null, _notification?: any, isFormatted?: boolean) => void;
    socket: Socket;
    warehouseTransfer?: IWarehouse_Transfer;
    warehouses: IWarehouse[];
};

export const TransfersSidebar = (props: Props) => {
    const supplierModel = useContext(SupplierContext);
    const userModel = useContext(UserContext);

    const [originWarehouseGuid, setOriginWarehouseGuid] = useState<string | undefined>();
    const [destinationWarehouseGuid, setDestinationWarehouseGuid] = useState<string | undefined>();
    const [saveActive, setSaveActive] = useState<boolean>(false);

    // Van-sell/DSD: vans are excluded from the default warehouse fetch, so the sidebar owns its own
    // warehouse list and re-fetches (with/without vehicles) as the toggle changes.
    const [warehouses, setWarehouses] = useState<IWarehouse[]>(props.warehouses);
    const [includeVehicles, setIncludeVehicles] = useState<boolean>(!!supplierModel.has_truck_inventory);

    const handleChange = (e: ChangeEvent<any>) => {
        const value = e.target.type === "checked" ? e.target.checked : e.target.value;
        const name = e.target.name;

        switch (name) {
            case "origin_warehouse_id":
                setOriginWarehouseGuid(value);
                break;

            case "destination_warehouse_id":
                setDestinationWarehouseGuid(value);
                break;

            case "include_vehicles":
                setIncludeVehicles(value);
                // Re-fetch the warehouse list so the dropdowns reflect whether vans are included.
                getWarehouses(value);
                // Clear selections to avoid keeping a warehouse that may no longer be available.
                setOriginWarehouseGuid(undefined);
                setDestinationWarehouseGuid(undefined);
                break;
        }
    };

    const getWarehouses = (withVehicles: boolean) => {
        const payload: IGetWarehousesDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            includeVehicles: withVehicles,
        };

        props.socket.emit(EndpointsEnum.WarehouseGetWarehouses, payload);
    };

    const onGetWarehouses = (data: IOnGetWarehousesDTO) => {
        setWarehouses(data.warehouses);
    };

    useEffect(() => {
        props.socket.on(EndpointsEnum.WarehouseOnProcessWarehouseTransfer, onProcessWarehouseTransfer);
        props.socket.on(EndpointsEnum.WarehouseOnGetWarehouses, onGetWarehouses);

        // The warehouses passed in were fetched without vans; refresh to include them when defaulting on.
        if (includeVehicles) {
            getWarehouses(true);
        }

        return () => {
            props.socket.removeListener(EndpointsEnum.WarehouseOnProcessWarehouseTransfer, onProcessWarehouseTransfer);
            props.socket.removeListener(EndpointsEnum.WarehouseOnGetWarehouses, onGetWarehouses);
        };
    }, []);

    const saveFn = () => {
        if (originWarehouseGuid && destinationWarehouseGuid) {
            const payload: IProcessWarehouseTransferDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                newTransfer: {
                    originWarehouseGuid,
                    destinationWarehouseGuid,
                },
            };

            props.socket.emit(EndpointsEnum.WarehouseProcessWarehouseTransfer, payload);

            setSaveActive(true);
        }
    };

    const onProcessWarehouseTransfer = () => {
        setSaveActive(false);
        props.cancelFn();
    };

    const cancel = () => {
        props.cancelFn();
    };

    return (
        <div className={"TransfersSidebar p-3"}>
            <div className={"row"}>
                <div className={"col-12"}>
                    <div className="text-end">
                        <button className="btn btn-danger btn-sm" onClick={cancel}>
                            <i className="far fa-times" />
                        </button>
                    </div>
                    <OwTitle className={"mb-2"} leftIconName={"fad fa-warehouse-alt"} title={"warehouse"} />
                    <hr />
                </div>
            </div>

            {supplierModel.has_truck_inventory ? (
                <div className={"mt-2"}>
                    <OwToggle name={"include_vehicles"} label={"include_vehicles"} active={includeVehicles} fn={handleChange} />
                </div>
            ) : null}

            <div className={"mt-2"}>
                <OwLabel title={"origin"} />
                <OwSelectOption name={"origin_warehouse_id"} fn={handleChange} options={warehouses} valueProp={"guid"} titleProp={"name"} />
            </div>

            <div className={"mt-2"}>
                <OwLabel title={"destination"} />
                <OwSelectOption
                    name={"destination_warehouse_id"}
                    fn={handleChange}
                    options={warehouses}
                    valueProp={"guid"}
                    titleProp={"name"}
                />
            </div>

            <div className={"mt-2"}>
                <OwButton title={"save"} fn={saveFn} active={saveActive} disableOnActive={true} />
            </div>
        </div>
    );
};
